/**
 * GroupMembersModal Component
 * Modal for managing group members - view, add, and remove members
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  SharingGroup,
  GroupMember,
  getGroupMembers,
  inviteMember,
  removeMember,
  updateMemberRole
} from '../services/sharingService';
import '../styles/components/menuBar.css'; // Use same menu bar styling
import '../styles/components/groupMembersModal.css';

interface GroupMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: SharingGroup | null;
  onUpdate?: () => void;
}

const GroupMembersModal: React.FC<GroupMembersModalProps> = ({
  isOpen,
  onClose,
  group,
  onUpdate
}) => {
  const { token } = useAuth();
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Add member form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'viewer' | 'editor' | 'owner'>('viewer');
  
  // Change role dialog
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [roleDialogMember, setRoleDialogMember] = useState<{ userId: number; displayName: string; currentRole: string } | null>(null);
  const [selectedRole, setSelectedRole] = useState<'viewer' | 'editor' | 'owner'>('viewer');

  useEffect(() => {
    if (isOpen && group) {
      loadMembers();
    }
  }, [isOpen, group]);

  // Scroll to form when it's shown
  useEffect(() => {
    if (showAddForm) {
      setTimeout(() => {
        const formElement = document.querySelector('.add-member-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  }, [showAddForm]);

  const loadMembers = async () => {
    if (!token || !group) return;
    
    try {
      setLoading(true);
      setError(null);
      const groupMembers = await getGroupMembers(token, group.id);
      setMembers(groupMembers);
    } catch (err: any) {
      setError(err.message || 'Failed to load members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token || !group || !newMemberEmail.trim()) {
      setError('Email is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const result = await inviteMember(token, group.id, {
        email: newMemberEmail.trim(),
        role: newMemberRole
      });

      if (result.status === 'added') {
        setSuccess(`${newMemberEmail} has been added to the group!`);
      } else if (result.status === 'invited') {
        setSuccess(`Invitation sent to ${newMemberEmail}!`);
      }

      // Reset form
      setNewMemberEmail('');
      setNewMemberRole('viewer');
      setShowAddForm(false);
      
      // Reload members
      await loadMembers();

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to invite member');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId: number, displayName: string) => {
    if (!token || !group) return;
    
    if (!window.confirm(`Remove ${displayName} from this group?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await removeMember(token, group.id, userId);
      setSuccess(`${displayName} has been removed from the group`);
      
      // Reload members
      await loadMembers();

      if (onUpdate) {
        onUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = (userId: number, displayName: string, currentRole: string) => {
    setRoleDialogMember({ userId, displayName, currentRole });
    setSelectedRole(currentRole as 'viewer' | 'editor' | 'owner');
    setShowRoleDialog(true);
  };

  const confirmRoleChange = async () => {
    if (!token || !group || !roleDialogMember) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await updateMemberRole(token, group.id, roleDialogMember.userId, selectedRole);
      setSuccess(`${roleDialogMember.displayName}'s role updated to ${selectedRole}`);
      
      // Reload members
      await loadMembers();

      if (onUpdate) {
        onUpdate();
      }
      
      setShowRoleDialog(false);
      setRoleDialogMember(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !group) return null;

  const isOwner = group.role === 'owner';
  const ownerCount = members.filter(m => m.role === 'owner').length;
  const isLastOwner = (member: GroupMember) => member.role === 'owner' && ownerCount === 1;

  return (
    <div className="group-members-modal-overlay">
      <div className="group-members-modal">
        {/* Top Menu Bar - Use same transparent style as ShareModal */}
        <div className="transparent-menu-bar group-members-menu-bar">
          <button className="back-arrow-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          {isOwner && (
            <button
              className="add-member-button"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <line x1="20" y1="8" x2="20" y2="14"></line>
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
              <span>Member</span>
            </button>
          )}
          <button className="done-button" onClick={onClose}>
            Done
          </button>
        </div>

        <div className="group-members-modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          {loading && !members.length ? (
            <div className="loading">Loading members...</div>
          ) : (
            <>
              <div className="members-list">
                {members.map((member) => (
                  <div key={member.user_id} className="member-item">
                    <div className="member-info">
                      <div className="member-name">{member.display_name}</div>
                      <div className="member-email">{member.email}</div>
                    </div>
                    <div className="member-actions">
                      <span className={`role-badge role-${member.role}`}>
                        {member.role}
                      </span>
                      {isOwner && !isLastOwner(member) && (
                        <>
                          <button
                            className="change-role-button"
                            onClick={() => handleChangeRole(member.user_id, member.display_name, member.role)}
                            disabled={loading}
                            title={isLastOwner(member) ? "Cannot change role of last owner" : "Change role"}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            className="remove-member-button"
                            onClick={() => handleRemoveMember(member.user_id, member.display_name)}
                            disabled={loading}
                            title="Remove member"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {isOwner && showAddForm && (
                <div className="add-member-section">
                  <form onSubmit={handleInviteMember} className="add-member-form">
                    <h3>Add Member</h3>
                    <div className="form-group">
                      <label htmlFor="memberEmail">Email Address *</label>
                      <input
                        id="memberEmail"
                        type="email"
                        value={newMemberEmail}
                        onChange={(e) => setNewMemberEmail(e.target.value)}
                        placeholder="user@example.com"
                        required
                        autoFocus
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="memberRole">Role *</label>
                      <select
                        id="memberRole"
                        value={newMemberRole}
                        onChange={(e) => setNewMemberRole(e.target.value as any)}
                      >
                        <option value="viewer">Viewer - Can view items</option>
                        <option value="editor">Editor - Can view and edit items</option>
                        <option value="owner">Owner - Full control</option>
                      </select>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewMemberEmail('');
                          setNewMemberRole('viewer');
                          setError(null);
                        }}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={loading || !newMemberEmail.trim()}
                      >
                        {loading ? 'Adding...' : 'Add Member'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {!isOwner && (
                <div className="info-message">
                  Only the group owner can add or remove members.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Role Change Dialog */}
      {showRoleDialog && roleDialogMember && (
        <div className="role-dialog-overlay" onClick={() => setShowRoleDialog(false)}>
          <div className="role-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Change role for {roleDialogMember.displayName}?</h3>
            <p className="current-role">Current: {roleDialogMember.currentRole}</p>
            
            <div className="role-select-container">
              <label htmlFor="role-select">Select new role:</label>
              <select
                id="role-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as 'viewer' | 'editor' | 'owner')}
                className="role-select"
                autoFocus
              >
                <option value="viewer">Viewer (Read-only)</option>
                <option value="editor">Editor (Can edit items)</option>
                <option value="owner">Owner (Full control)</option>
              </select>
            </div>

            <div className="role-dialog-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowRoleDialog(false);
                  setRoleDialogMember(null);
                }}
              >
                Cancel
              </button>
              <button
                className="ok-button"
                onClick={confirmRoleChange}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupMembersModal;
